import React from 'react'
import PlayerVideoControls from 'player/components/PlayerVideoControls'
import classNames from 'classnames'
import {useFullScreenToggle} from 'powtoon-commons/hooks'
import {Segment} from './types'
import {ViewEvents, ExternalController} from '../PlayerIFrame/types'
import {Overlay} from '../Overlay'
import {usePlayerVideoControls} from '../PlayerVideoControls/usePlayerVideoControls'
import {useSegmentRefsControls} from './useSegmentRefsControls'
import Preloader from '../Preloader'
import {isMuted} from './mutedPropertyHack'
import {createLogger} from 'logging'
import './SegmentsPlayer.scss'
const logger = createLogger('segments-player', 'SegmentsPlayer')

const noop = () => undefined
const noopExternalController = {
  play: noop,
  pause: noop,
  jumpToStart: noop,
  setShowControls: noop,
  getTotalDuration: () => 0
}
interface MultiVideoPlayerProps extends ViewEvents {
  segments: Segment[]
  isLoading: boolean
  hideControls?: boolean
}
interface ShowControlsOverride {
  override: boolean
  showControls: boolean
}

const MultiVideoPlayer: React.FC<MultiVideoPlayerProps> = ({
  segments,
  isLoading,
  hideControls = false,
  onPlay = noop,
  onPlayerReady = noop,
  onTotalTimeChanged = noop,
  onEnd = noop
}) => {
  const mediaControls = useSegmentRefsControls({segments, isLoading})
  const videoControls = usePlayerVideoControls()
  const [reachedEnd, setReachedEnd] = React.useState(false)
  const [scrolledWhilePlay, setScrolledWhilePlay] = React.useState(false)
  const mainRef = React.useRef(null)
  const {isPlaying} = videoControls
  const {
    ready, mediaRefs, activePart, segmentedTimeMapping, checkIfReady, onProgress, onSeeked, isBuffering
  } = mediaControls
  const [showControlsOverride, setShowControlsOverride] = React.useState<ShowControlsOverride>({
    override: false,
    showControls: !hideControls
  })

  const externalController = React.useRef<ExternalController>(noopExternalController)

  React.useEffect(
    () => {
      if (segmentedTimeMapping.length > 0 && ready) {

        const totalDuration = segmentedTimeMapping.reduce((total, {duration, encodingStartTime}, index) => {
          if (index === 0) {
            return duration
          }
          else {
            return total + duration
          }
        }, 0)
        videoControls.setTotalSeconds(totalDuration)
        onPlayerReady(externalController.current)
        onScrollbarChange(0)
      }
    },
    [segmentedTimeMapping, ready]
  )

  const onVideoEnd = React.useCallback(
    index => () => {
      if (index < mediaRefs.length - 1) {
        mediaControls.setPartCurrentTime(index + 1, 0)
        mediaControls.updatePartPlayState('play', index + 1)
        videoControls.setIsPlaying(true)
      }
      else {
        setReachedEnd(true)
        videoControls.setIsPlaying(false)
        videoControls.setProgress(videoControls.totalSeconds)
      }
    },
    [mediaRefs, isLoading, mediaControls, videoControls]
  )

  const onTimeUpdate = React.useCallback(
    index => () => {
      if (index === activePart && isPlaying) {
        try {
          const actualCurrentTime: number = (mediaRefs[index].video.current?.currentTime || 0)
          const offset: number = actualCurrentTime - segmentedTimeMapping[activePart].encodingStartTime
          videoControls.setProgress(segmentedTimeMapping[activePart].startTime + offset)
        }
        catch (error) {
          logger.error(error)
        }
      }
    },
    [mediaRefs, activePart, isPlaying]
  )
  const onPlayToggle = React.useCallback(() => {
    if (segmentedTimeMapping.length === 0 || isBuffering) {return}

    const nextPlayState = !isPlaying
    setReachedEnd(false)
    if (reachedEnd && nextPlayState) {
      const {encodingStartTime, startTime} = segmentedTimeMapping[0]
      const offset = encodingStartTime - startTime
      mediaControls.setPartCurrentTime(0, offset)
      mediaControls.updatePartPlayState(nextPlayState ? 'play' : 'pause', 0)
      videoControls.setProgress(0)
      videoControls.setIsPlaying(nextPlayState)
      onPlay()
    }
    else {
      mediaControls.updatePartPlayState(nextPlayState ? 'play' : 'pause', activePart)
      videoControls.setIsPlaying(nextPlayState)
      if (nextPlayState) {
        onPlay()
      } else {
        onEnd()
        onTotalTimeChanged(videoControls.progress)
      }
    }
  }, [videoControls, mediaControls, reachedEnd, segmentedTimeMapping, isBuffering])

  const onAfterScrollbarChange = React.useCallback(() => {
    if (scrolledWhilePlay) {
      onPlayToggle()
    }
    setScrolledWhilePlay(false)
  }, [scrolledWhilePlay])

  const onScrollbarChange = React.useCallback(
    (pos: number) => {
      let found = false
      segmentedTimeMapping.forEach(({startTime, encodingStartTime, duration}, index) => {
        if (!found && pos < startTime + duration) {
          found = true
          const offset = encodingStartTime - startTime
          mediaControls.setPartCurrentTime(index, pos + offset)
        }
        videoControls.setIsPlaying(false)
      })
      videoControls.setProgress(pos)
      if (videoControls.isPlaying) {
        setScrolledWhilePlay(true)
      }
    },
    [mediaControls, videoControls, segmentedTimeMapping]
  )

  const onVolumeChange = React.useCallback(
    newVolume => {
      mediaControls.setVolume(newVolume)
      videoControls.setVolume(newVolume)
    },
    [mediaRefs]
  )

  // keep controller updated
  React.useEffect(
    () => {
      externalController.current.play = () => !isPlaying && onPlayToggle()
      externalController.current.pause = () => isPlaying && onPlayToggle()
      externalController.current.jumpToStart = () => onScrollbarChange(0)
      externalController.current.setShowControls = show => setShowControlsOverride({override: true, showControls: show})
      externalController.current.getTotalDuration = () => videoControls.totalSeconds
    },
    [isPlaying, onScrollbarChange, onPlayToggle, videoControls]
  )

  const {isFullScreen, toggleFullscreen} = useFullScreenToggle(mainRef)
  const showControls = showControlsOverride.override ? showControlsOverride.showControls : !hideControls

  return (
    <div styleName={classNames('container', {playing: isPlaying})} ref={mainRef}>
      {isLoading
        ? (
          <Preloader />
        )
        : (
          mediaRefs.map(({video: videoRef, audio: audioRef}, index) => {
            const {video_url: videoSrc, audio_url: audioSrc} = segments[index]
            const selected = index === activePart
            return (
              <React.Fragment key={audioSrc + index}>
                <video
                  key={audioSrc + index}
                  ref={videoRef}
                  onSeeked={onSeeked(index)}
                  onClick={onPlayToggle}
                  onCanPlay={checkIfReady}
                  onTimeUpdate={onTimeUpdate(index)}
                  onEnded={onVideoEnd(index)}
                  onProgress={onProgress(index)}
                  onDoubleClick={toggleFullscreen}
                  styleName={classNames('video-source', {selected})}
                  muted={isMuted}
                >
                  <source src={videoSrc} type="video/mp4" />
                </video>
                {!!audioSrc && (
                  <audio
                    ref={audioRef}
                    onCanPlay={checkIfReady}
                    styleName={classNames('audio-source', {selected})}
                  >
                    <source src={audioSrc} type="audio/ogg" />
                  </audio>
                )}
              </React.Fragment>
            )
          })
        )}
      <Overlay
        isVisible={!isPlaying && !!videoControls.progress}
        onClick={onPlayToggle}
        onDoubleClick={toggleFullscreen}
      />
      {!ready && !isLoading  && <Preloader />}
      {isBuffering && <div styleName="buffering"><div styleName="buffering-loader"/></div>}
      {showControls && (
        <div styleName="controls-container">
          <PlayerVideoControls
            onPlayToggle={onPlayToggle}
            isFullScreen={isFullScreen}
            onScrollbarChange={onScrollbarChange}
            onAfterScrollbarChange={onAfterScrollbarChange}
            showRestartAtEnd={true}
            onVolumeChange={onVolumeChange}
            onFullScreenToggle={toggleFullscreen}
            {...videoControls}
          />
        </div>
      )}
    </div>
  )
}

export default MultiVideoPlayer
